import * as React from 'react';
import DatevService, { DatevConnectionStatus, DatevClient } from '../services/DatevService';

const DATEV_AUTH_HOSTS = [
  'login.datev.de',
  'secure8.datev.de', // manche Tenants bekommen secureX.* zurück
];

async function openDatevPopup(getAuthorizeUrl: () => Promise<string | undefined>): Promise<Window> {
  // 1) Popup öffnen, damit Browser es nicht blockt
  const popup = window.open('about:blank', 'datev_oauth', 'width=860,height=800');
  if (!popup) {
    throw new Error('Popup blockiert. Bitte erlauben Sie Popups für diese Seite.');
  }

  // 2) Authorize URL laden
  const authorize_url = await getAuthorizeUrl();

  if (!authorize_url) {
    popup.close();
    throw new Error('Keine Authorize-URL vom Server erhalten.');
  }

  try {
    const url = new URL(authorize_url);
    if (!DATEV_AUTH_HOSTS.includes(url.host)) {
      popup.close();
      throw new Error(`Unerwartete Authorize-Domain: ${url.host}`);
    }
  } catch {
    popup.close();
    throw new Error('Authorize-URL ungültig.');
  }

  // 3) Popup auf DATEV umschalten
  popup.location.replace(authorize_url);
  return popup;
}

export function DatevIntegration(): React.JSX.Element {
  // Popup-Modus erkennen
  const isPopup = typeof window !== 'undefined' && !!window.opener;

  const [status, setStatus] = React.useState<DatevConnectionStatus | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [connecting, setConnecting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [success, setSuccess] = React.useState<string>('');

  const [env, setEnv] = React.useState<'sandbox' | 'production'>('sandbox');
  const [beraterNr, setBeraterNr] = React.useState<string>('');
  const [mandantenNr, setMandantenNr] = React.useState<string>('');
  const [validationError, setValidationError] = React.useState<string>('');

  const [clients, setClients] = React.useState<DatevClient[]>([]);
  const [loadingClients, setLoadingClients] = React.useState<boolean>(false);
  const [choosingClient, setChoosingClient] = React.useState<boolean>(false);
  const [invalidStateError, setInvalidStateError] = React.useState<boolean>(false);
  const [clientsTraceId, setClientsTraceId] = React.useState<string | null>(null);

  // Refs for popup and polling (shared between handleConnect and postMessage)
  const pollRef = React.useRef<number | undefined>(undefined);
  const popupRef = React.useRef<Window | null>(null);

  // Validation regex
  const BERATER_NR_REGEX = /^\d{7}$/;
  const MANDANTEN_NR_REGEX = /^\d{5}$/;

  const validateNumbers = (): boolean => {
    setValidationError('');

    // Sandbox: keine Nummern erforderlich
    if (env === 'sandbox') {
      return true;
    }

    // Produktion: Validierung erforderlich
    if (!mandantenNr.trim()) {
      setValidationError('Mandanten-Nr. ist erforderlich');
      return false;
    }

    if (!MANDANTEN_NR_REGEX.test(mandantenNr.trim())) {
      setValidationError('Mandanten-Nr. muss genau 5 Ziffern enthalten');
      return false;
    }

    if (beraterNr.trim() && !BERATER_NR_REGEX.test(beraterNr.trim())) {
      setValidationError('Berater-Nr. muss genau 7 Ziffern enthalten');
      return false;
    }

    return true;
  };

  const loadStatus = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await DatevService.getConnectionStatus();
      setStatus(data);
      // Support both field name conventions
      const berater = data.consultant_number || data.berater_nr;
      const mandanten = data.client_number || data.mandanten_nr;
      if (berater) setBeraterNr(berater);
      if (mandanten) setMandantenNr(mandanten);
    } catch (e: any) {
      const status = e?.response?.status;

      // 404: Backend-Route existiert, aber Status-Endpunkt falsch oder noch nicht verbunden
      // → Zeige "Nicht verbunden" (kein Error-Banner!)
      if (status === 404) {
        setStatus({ connected: false });
      }
      // Nur echte Fehler (401/403/500) als Error-Banner zeigen
      else if (status === 401) {
        setError('Nicht authentifiziert. Bitte melden Sie sich erneut an.');
      } else if (status === 403) {
        setError('Keine Berechtigung zum Abrufen des DATEV-Status.');
      } else {
        // 500 oder Netzwerkfehler
        const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Laden des Status';
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Popup-Callback-Handler: Sendet postMessage und schließt Popup
  React.useEffect(() => {
    if (!isPopup) return; // Nur im Popup ausführen

    const params = new URLSearchParams(window.location.search);
    const status = params.get('datev');
    const reason = params.get('reason');

    console.log('[DATEV popup] detected, status:', status, 'reason:', reason);

    if (status === 'connected') {
      console.log('[DATEV popup] sending DATEV_CONNECTED');
      window.opener?.postMessage('DATEV_CONNECTED', '*');
      window.close();
    } else if (status === 'error') {
      console.log('[DATEV popup] sending DATEV_OAUTH_ERROR with reason:', reason);
      window.opener?.postMessage({ type: 'DATEV_OAUTH_ERROR', reason }, '*');
      window.close();
    }
    // Default: Diese Seite im Popup gar nicht erst "anzeigen"
  }, [isPopup]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('datev') === 'error' && params.get('reason') === 'invalid_state') {
      setInvalidStateError(true);
      setError('Sitzung abgelaufen oder ungültig. Bitte erneut verbinden.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  React.useEffect(() => {
    function onMsg(ev: MessageEvent) {
      if (!ev || !ev.data) return;

      // Debug-Log für postMessage-Empfang
      console.log('[DATEV parent] message:', ev.data);

      if (ev.data === 'DATEV_CONNECTED') {
        // Polling stoppen
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = undefined;
        }
        // Popup schließen
        try {
          popupRef.current?.close();
        } catch {}
        popupRef.current = null;

        setConnecting(false);
        setStatus((prevStatus) => ({ ...prevStatus, connected: true } as DatevConnectionStatus));
        setSuccess('DATEV-Verbindung erfolgreich hergestellt!');
        // Status neu laden
        loadStatus();
      } else if (typeof ev.data === 'object' && ev.data?.type === 'DATEV_OAUTH_ERROR') {
        // Polling stoppen
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = undefined;
        }
        // Popup schließen
        try {
          popupRef.current?.close();
        } catch {}
        popupRef.current = null;

        setConnecting(false);
        const reason = ev.data.reason ?? 'unbekannt';
        if (reason === 'invalid_state') {
          setInvalidStateError(true);
          setError('Sitzung abgelaufen oder ungültig. Bitte erneut verbinden.');
        } else {
          setError(`DATEV Login fehlgeschlagen: ${reason}`);
        }
      }
    }

    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [loadStatus]);

  const handleConnect = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();

    if (!validateNumbers()) {
      return;
    }

    setError('');
    setSuccess('');
    setValidationError('');
    setInvalidStateError(false);
    setConnecting(true);

    try {
      popupRef.current = await openDatevPopup(async () => {
        const payload: any = { env };

        // Nur in Produktion Nummern mitschicken
        if (env === 'production') {
          payload.consultant_number = beraterNr.trim() || undefined;
          payload.client_number = mandantenNr.trim();
        }

        const response = await DatevService.startConnection(payload);
        return response?.authorize_url;
      });

      // Polling schlank halten – stoppt, sobald connected
      pollRef.current = window.setInterval(async () => {
        try {
          const s = await DatevService.getConnectionStatus();
          if (s?.connected) {
            if (pollRef.current) {
              window.clearInterval(pollRef.current);
              pollRef.current = undefined;
            }
            setConnecting(false);
            setStatus(s);
            setSuccess('DATEV-Verbindung erfolgreich hergestellt!');
            try {
              popupRef.current?.close();
            } catch {}
            popupRef.current = null;
          }
        } catch {
          // 404 ignorieren, weiter pollen
        }
      }, 1500);
    } catch (err: any) {
      setConnecting(false);
      try {
        popupRef.current?.close();
      } catch {}
      popupRef.current = null;
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = undefined;
      }
      setError(err?.message ?? 'Unbekannter Fehler beim Starten der Verbindung.');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('DATEV-Verbindung wirklich trennen?')) return;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await DatevService.disconnect();
      // Cache invalidation: reload status from backend to ensure UI is in sync
      await loadStatus();
      setClients([]);
      setSuccess('DATEV-Verbindung getrennt');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Trennen';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadClients = async () => {
    setLoadingClients(true);
    setError('');
    setValidationError('');
    setClientsTraceId(null);
    try {
      const response = await DatevService.getClients();
      setClients(response.clients || []);
      setClientsTraceId(response.trace_id || null);

      if (response.clients.length === 0) {
        setValidationError('Keine Clients gefunden. Bitte prüfen Sie die Berechtigungen in DATEV.');
      } else {
        setSuccess(`${response.count} Client(s) geladen`);
        // Reload status to update default_client_id badge
        await loadStatus();
      }
    } catch (e: any) {
      const errorMessage = e?.message || '';
      const status = e?.response?.status;
      const trace = e?.trace_id || e?.response?.data?.trace_id;

      // 401: Session expired in SundaeOS
      if (status === 401) {
        setError('Sitzung abgelaufen – bitte in SundaeOS neu anmelden.');
        return;
      }

      // 403: No rights to access clients
      if (status === 403 || errorMessage === 'no-client-rights') {
        setValidationError(`⚠️ Kein Zugriff auf Clients – bitte Rechte im DATEV-Konto prüfen. Fehler-ID: ${trace || '—'}`);
        setClientsTraceId(trace || null);
        return;
      }

      // 5xx: Technical error at DATEV or backend
      if (status && status >= 500) {
        setError(`Technischer Fehler bei DATEV (${status}). Fehler-ID: ${trace || '—'}. Bitte später erneut versuchen.`);
        setClientsTraceId(trace || null);
        return;
      }

      // Fallback for other errors
      if (errorMessage === 'clients-technical-error') {
        setError(`Technischer Fehler beim Abruf der Clients (${status || '—'}). Fehler-ID: ${trace || '—'}. Bitte später erneut.`);
      } else {
        setError(`Technischer Fehler beim Abruf der Clients. Fehler-ID: ${trace || '—'}. Bitte später erneut.`);
      }

      setClientsTraceId(trace || null);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleChooseClient = async (clientId: string) => {
    setChoosingClient(true);
    setError('');
    try {
      await DatevService.chooseClient({ client_id: clientId });
      await loadStatus(); // Reload status to get updated default_client_id
      setSuccess('Standard-Client erfolgreich gesetzt');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Setzen des Standard-Clients';
      setError(detail);
    } finally {
      setChoosingClient(false);
    }
  };

  // Popup-Modus: UI nicht rendern, nur postMessage senden
  if (isPopup) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: '#666' }}>Verbindung wird hergestellt...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <h1 style={{ fontSize: 24, marginBottom: 8, color: '#0f2b5a' }}>DATEV Integration</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
        Verbinden Sie Ihr DATEV Lohn & Gehalt oder LODAS System für automatischen Datenaustausch.
      </p>
      <p style={{ fontSize: 13, color: '#999', marginBottom: 24, fontStyle: 'italic' }}>
        Erfordert DATEV SmartLogin der berechtigten Person.
      </p>

      {validationError && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 6,
            color: '#856404',
            fontSize: 14,
          }}
        >
          <div style={{ marginBottom: status?.connected ? 12 : 0 }}>
            ⚠️ {validationError}
          </div>
          {status?.connected && (
            <button
              type="button"
              onClick={handleLoadClients}
              disabled={loadingClients}
              style={{
                padding: '6px 12px',
                background: loadingClients ? '#ccc' : '#ffc107',
                color: '#000',
                border: 'none',
                borderRadius: 4,
                cursor: loadingClients ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {loadingClients ? 'Lade...' : 'Nochmal versuchen'}
            </button>
          )}
        </div>
      )}

      {invalidStateError && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 6,
            color: '#c62828',
            fontSize: 14,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            ⚠️ Sitzung abgelaufen oder ungültig. Bitte erneut verbinden (neues Fenster öffnet sich).
          </div>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            style={{
              padding: '8px 16px',
              background: '#021a4a',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: connecting ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Erneut verbinden
          </button>
        </div>
      )}

      {error && !invalidStateError && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 6,
            color: '#c62828',
            fontSize: 14,
          }}
        >
          <div style={{ marginBottom: status?.connected ? 12 : 0 }}>
            {error}
          </div>
          {status?.connected && (
            <button
              type="button"
              onClick={handleLoadClients}
              disabled={loadingClients}
              style={{
                padding: '6px 12px',
                background: loadingClients ? '#ccc' : '#ef5350',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: loadingClients ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {loadingClients ? 'Lade...' : 'Nochmal versuchen'}
            </button>
          )}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#e8f5e9',
            border: '1px solid #66bb6a',
            borderRadius: 6,
            color: '#2e7d32',
            fontSize: 14,
          }}
        >
          ✓ {success}
        </div>
      )}

      {/* Connection Card */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e0e3ef',
          borderRadius: 8,
          padding: 20,
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f2b5a', margin: 0 }}>
            Mit DATEV verbinden
          </h2>
          {loading ? (
            <span style={{ fontSize: 13, color: '#999' }}>Lädt...</span>
          ) : status?.connected ? (
            <span
              style={{
                padding: '4px 12px',
                background: '#e8f5e9',
                color: '#2e7d32',
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              ✓ Verbunden
              {status.connected_at && ` seit ${new Date(status.connected_at).toLocaleDateString('de-DE')}`}
            </span>
          ) : (
            <span
              style={{
                padding: '4px 12px',
                background: '#f5f5f5',
                color: '#666',
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Nicht verbunden
            </span>
          )}
        </div>

        {/* Environment Toggle */}
        {!status?.connected && (
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
              }}
            >
              Umgebung
            </label>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={() => setEnv('sandbox')}
                disabled={connecting}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: env === 'sandbox' ? '#021a4a' : '#fff',
                  color: env === 'sandbox' ? '#fff' : '#374151',
                  border: `2px solid ${env === 'sandbox' ? '#021a4a' : '#d0d4e4'}`,
                  borderRadius: 6,
                  cursor: connecting ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Sandbox
              </button>
              <button
                type="button"
                onClick={() => setEnv('production')}
                disabled={connecting}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: env === 'production' ? '#021a4a' : '#fff',
                  color: env === 'production' ? '#fff' : '#374151',
                  border: `2px solid ${env === 'production' ? '#021a4a' : '#d0d4e4'}`,
                  borderRadius: 6,
                  cursor: connecting ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Produktion
              </button>
            </div>
          </div>
        )}

        {/* Nummern-Felder nur in Produktion */}
        {!status?.connected && env === 'production' && (
          <>
            <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              color: '#374151',
              marginBottom: 6,
            }}
          >
            Berater-Nr. (7-stellig) <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={7}
            value={beraterNr}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setBeraterNr(value);
              setValidationError('');
            }}
            disabled={status?.connected || connecting}
            placeholder="z.B. 1234567"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d0d4e4',
              borderRadius: 6,
              fontSize: 14,
              outline: 'none',
              background: status?.connected ? '#f9fafb' : '#fff',
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              color: '#374151',
              marginBottom: 6,
            }}
          >
            Mandanten-Nr. (5-stellig) <span style={{ color: '#ef5350' }}>*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            value={mandantenNr}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setMandantenNr(value);
              setValidationError('');
            }}
            disabled={status?.connected || connecting}
            placeholder="z.B. 12345"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d0d4e4',
              borderRadius: 6,
              fontSize: 14,
              outline: 'none',
              background: status?.connected ? '#f9fafb' : '#fff',
            }}
          />
        </div>
          </>
        )}

        {/* Sandbox-Hinweis */}
        {!status?.connected && env === 'sandbox' && (
          <div
            style={{
              padding: 12,
              marginBottom: 16,
              background: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: 6,
              fontSize: 13,
              color: '#0d47a1',
            }}
          >
            ℹ️ In der Sandbox sind keine Berater- oder Mandanten-Nummern erforderlich.
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          {!status?.connected ? (
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting || (env === 'production' && (!mandantenNr.trim() || mandantenNr.length !== 5))}
              style={{
                padding: '10px 20px',
                background: connecting || (env === 'production' && (!mandantenNr.trim() || mandantenNr.length !== 5)) ? '#ccc' : '#021a4a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: connecting || (env === 'production' && (!mandantenNr.trim() || mandantenNr.length !== 5)) ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {connecting ? 'Verbinde...' : 'Mit DATEV verbinden'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: '#ef5350',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Verbindung trennen
            </button>
          )}
        </div>
      </div>

      {status?.connected && (
        <>
          <div
            style={{
              padding: 16,
              background: '#f8f9fa',
              border: '1px solid #e0e3ef',
              borderRadius: 6,
              fontSize: 13,
              color: '#495057',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Verbindungsdetails:</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
              {(status.consultant_number || status.berater_nr) && (
                <>
                  <div>Berater-Nr.:</div>
                  <div>{status.consultant_number || status.berater_nr}</div>
                </>
              )}
              <div>Mandanten-Nr.:</div>
              <div>{status.client_number || status.mandanten_nr}</div>
              {status.env && (
                <>
                  <div>Umgebung:</div>
                  <div style={{ textTransform: 'capitalize' }}>{status.env}</div>
                </>
              )}
              {status.expires_at && (
                <>
                  <div>Token läuft ab:</div>
                  <div>{new Date(status.expires_at).toLocaleString('de-DE')}</div>
                </>
              )}
              {status.connected_at && (
                <>
                  <div>Letzte Aktualisierung:</div>
                  <div>{new Date(status.connected_at).toLocaleString('de-DE')}</div>
                </>
              )}
              {status.product && (
                <>
                  <div>Produkt:</div>
                  <div>{status.product}</div>
                </>
              )}
              {status.transport && (
                <>
                  <div>Transport:</div>
                  <div>{status.transport === 'manual' ? 'Manuell' : status.transport}</div>
                </>
              )}
            </div>
          </div>
          <div
            style={{
              padding: 12,
              marginTop: 12,
              background: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: 6,
              fontSize: 13,
              color: '#0d47a1',
            }}
          >
            <div style={{ marginBottom: 4 }}>
              ℹ️ Sie können Ihre verbundenen Anwendungen in DATEV verwalten:
            </div>
            <a
              href="https://apps.datev.de/tokrevui"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#1976d2',
                textDecoration: 'underline',
                fontWeight: 600,
              }}
            >
              Verbundene Anwendungen öffnen →
            </a>
          </div>
        </>
      )}

      {/* UX-Hinweise je nach State */}
      {connecting && !status?.connected && (
        <div
          style={{
            padding: 16,
            marginTop: 16,
            background: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: 6,
            fontSize: 14,
            color: '#0d47a1',
          }}
        >
          ℹ️ Login im Popup abschließen... Polling läuft.
        </div>
      )}

      {/* Kritischer Fehler: token_client_mismatch */}
      {status?.connected && status.last_error === 'token_client_mismatch' && (
        <div
          style={{
            padding: 16,
            marginTop: 16,
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 6,
            fontSize: 14,
            color: '#c62828',
          }}
        >
          <div style={{ marginBottom: 12 }}>
            ⚠️ DATEV-Konfiguration wurde geändert. Die Verbindung ist nicht mehr gültig.
            {status.trace_id && (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Fehler-ID: {status.trace_id}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={async () => {
              await handleDisconnect();
              // UI will automatically refresh after disconnect
            }}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: loading ? '#ccc' : '#021a4a',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {loading ? 'Trenne...' : 'Neu verbinden'}
          </button>
        </div>
      )}

      {/* Gelber Hinweis wenn Clients nicht erreichbar */}
      {status?.connected && status.clients_reachable === false && (
        <div
          style={{
            padding: 16,
            marginTop: 16,
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 6,
            fontSize: 14,
            color: '#856404',
          }}
        >
          <div style={{ marginBottom: 12 }}>
            ⚠️ Clients noch nicht erreichbar oder Fehler beim letzten Versuch.
            {status.last_error && (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <strong>Fehler:</strong> {status.last_error}
              </div>
            )}
            {status.trace_id && (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Fehler-ID: {status.trace_id}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleLoadClients}
            disabled={loadingClients}
            style={{
              padding: '8px 16px',
              background: loadingClients ? '#ccc' : '#ffc107',
              color: '#000',
              border: 'none',
              borderRadius: 4,
              cursor: loadingClients ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {loadingClients ? 'Lade...' : 'Clients laden (Retry)'}
          </button>
        </div>
      )}

      {/* Clients-Sektion - immer anzeigen wenn verbunden */}
      {status?.connected && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e3ef',
            borderRadius: 8,
            padding: 20,
            marginTop: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f2b5a', margin: 0 }}>DATEV Clients</h2>
            {status.default_client_name && (
              <span
                style={{
                  padding: '4px 12px',
                  background: '#e8f5e9',
                  color: '#2e7d32',
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Standard: {status.default_client_name}
              </span>
            )}
          </div>

          {clients.length === 0 ? (
            <button
              type="button"
              onClick={handleLoadClients}
              disabled={loadingClients}
              style={{
                padding: '10px 20px',
                background: loadingClients ? '#ccc' : '#021a4a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: loadingClients ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {loadingClients ? 'Lade Clients...' : 'Clients laden'}
            </button>
          ) : (
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e3ef' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 13, color: '#666' }}>ID</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 13, color: '#666' }}>Name</th>
                    {clients.some(c => c.number) && (
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: 13, color: '#666' }}>Nummer</th>
                    )}
                    <th style={{ padding: '8px', textAlign: 'right', fontSize: 13, color: '#666' }}>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => {
                    const isDefault = status.default_client_id === client.id;
                    return (
                      <tr
                        key={client.id}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          background: isDefault ? '#f8f9fa' : '#fff',
                        }}
                      >
                        <td style={{ padding: '12px 8px', fontSize: 14 }}>{client.id}</td>
                        <td style={{ padding: '12px 8px', fontSize: 14, fontWeight: isDefault ? 600 : 400 }}>
                          {client.name}
                          {isDefault && (
                            <span
                              style={{
                                marginLeft: 8,
                                padding: '2px 8px',
                                background: '#e8f5e9',
                                color: '#2e7d32',
                                borderRadius: 8,
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              Standard
                            </span>
                          )}
                        </td>
                        {clients.some(c => c.number) && (
                          <td style={{ padding: '12px 8px', fontSize: 14 }}>{client.number || '-'}</td>
                        )}
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          {!isDefault && (
                            <button
                              type="button"
                              onClick={() => handleChooseClient(client.id)}
                              disabled={choosingClient}
                              style={{
                                padding: '6px 12px',
                                background: '#fff',
                                color: '#021a4a',
                                border: '1px solid #021a4a',
                                borderRadius: 4,
                                cursor: choosingClient ? 'not-allowed' : 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                              }}
                            >
                              Als Standard wählen
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DatevIntegration;
